import { Fragment, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiExpandableBox,
  MiIcon,
  MiStackedTypography,
} from "@miplatform/components";
import {
  IHome,
  IOrder,
  IOrderInfo,
  IWorkOrder,
  WorkOrderStatus,
} from "@miplatform/types";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { mdiFileDocumentOutline } from "@mdi/js";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { MiAbbrWorkOrderCard } from "../../../../components/MiWorkOrderCard/MiAbbrWorkOrderCard";
import { OrderInfoCardEmails } from "./OrderInfoCardEmails/OrderInfoCardEmails";

interface OrderInfoCardProps {
  order: IOrderInfo | IOrder;
  home?: IHome | null;
  fieldsConfig?: { label: string; fieldKey: string }[];
  hideEmails?: boolean;
  onClose?: () => void;
  holdType?: string;
}

export const OrderInfoCard = memo(
  ({
    order,
    home = (order as IOrder).home,
    fieldsConfig,
    hideEmails,
    onClose,
    holdType,
  }: OrderInfoCardProps) => {
    const { orderTemplateName, orderStatus, workOrders = [] } = order;
    const notes = (order as IOrder).notes ?? (order as IOrderInfo).orderNotes;
    const { theme, classes } = useStyles({ order });
    const homeAddressFields = [
      home?.address?.city,
      home?.address?.regionCode,
      home?.communityName,
      home?.builderName,
    ];
    const completedWorkOrdersCount = useMemo(
      () =>
        workOrders.filter(({ status }) => status === WorkOrderStatus.Complete)
          .length,
      [workOrders]
    );

    const renderFields = () =>
      fieldsConfig?.map(({ label, fieldKey }) => (
        <Grid key={fieldKey} size={{ xs: 6 }}>
          <MiStackedTypography
            title={label}
            description={parseFieldValue(getNestedValue(order, fieldKey))}
          />
        </Grid>
      ));

    const renderHomeInfo = () =>
      homeAddressFields.filter(Boolean).map((field, i) => (
        <Fragment key={field}>
          {field}
          {i !== homeAddressFields.length - 1 && " | "}
        </Fragment>
      ));

    return (
      <Paper className={classes.paper} onClick={(e) => e.stopPropagation()}>
        <Box className={classes.fixedHeader}>
          <Box className={classes.header}>
            <Typography className={classes.headerText}>
              {orderTemplateName}
            </Typography>
            <Typography className={classes.headerText}>
              {orderStatus}
            </Typography>
          </Box>
          <Box className={classes.topContent}>
            <Typography className={classes.addressText}>
              {home?.address?.addressLine1}
            </Typography>
            <Typography className={classes.fieldText}>
              {renderHomeInfo()}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.scrollableContent}>
          {fieldsConfig && fieldsConfig.length ? (
            <Grid container className={classes.middleContent}>
              {renderFields()}
            </Grid>
          ) : null}
          {workOrders.length !== 0 && (
            <MiExpandableBox
              defaultExpanded
              title="Work Orders"
              sx={{ pt: 2 }}
              rightHeaderContent={
                <Box display="flex" alignItems="center" gap={1}>
                  <MiIcon
                    path={mdiFileDocumentOutline}
                    size={0.6}
                    color={theme.palette.warning.main}
                    style={{
                      marginBottom: theme.spacing(0.5),
                    }}
                  />
                  <Typography
                    fontSize={theme.fontSize.small}
                    fontWeight={theme.fontWeight.semiBold}
                  >
                    {completedWorkOrdersCount}/{workOrders.length}
                  </Typography>
                </Box>
              }
            >
              <Box display="flex" flexDirection="column" gap={1}>
                {workOrders.map((workOrder) => (
                  <MiAbbrWorkOrderCard
                    key={workOrder.id}
                    workOrder={workOrder as IWorkOrder}
                  />
                ))}
              </Box>
            </MiExpandableBox>
          )}
          {!hideEmails && (
            <OrderInfoCardEmails order={order} onClose={onClose} />
          )}
          <MiStackedTypography
            className={classes.bottomContent}
            title="Order Notes"
            description={notes || "--"}
            descriptionProps={{
              style: {
                whiteSpace: "initial",
              },
            }}
          />
          <MiStackedTypography
            className={classes.bottomContent}
            title="Hold Type"
            description={holdType || "--"}
            descriptionProps={{
              style: {
                whiteSpace: "initial",
              },
            }}
          />
        </Box>
      </Paper>
    );
  }
);

const useStyles = makeStyles<{ order?: IOrderInfo | IOrder }>()(
  (theme, { order = {} }) => ({
    paper: {
      position: "relative",
      borderRadius: theme.spacing(1.5),
      width: 374,
      border: `1px solid ${
        order?.orderTemplateColor || theme.palette.primary.main
      }`,
      maxHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    fixedHeader: {
      flexShrink: 0,
    },
    scrollableContent: {
      flex: 1,
      overflowY: "auto",
    },

    header: {
      backgroundColor: order?.orderTemplateColor || theme.palette.primary.main,
      padding: theme.spacing(1, 2, 1, 2),
      display: "flex",
      justifyContent: "space-between",
      maxWidth: "100%",
      position: "relative",
    },
    headerText: {
      fontSize: theme.fontSize.small,
      color: theme.palette.primary.contrastText,
    },
    topContent: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    },
    addressText: {
      fontSize: theme.fontSize.medium,
      weight: theme.fontWeight.semiBold,
    },
    fieldText: {
      display: "flex",
      fontSize: theme.fontSize.small,
      weight: theme.fontWeight.regular,
      color: theme.palette.grey.main,
    },
    middleContent: {
      spacing: theme.spacing(2),
      padding: theme.spacing(1, 2),
      backgroundColor: theme.palette.grey["100"],
    },
    bottomContent: {
      padding: theme.spacing(2),
      maxWidth: "100%",
    },
  })
);
